import moment from 'moment'
import { isNil, groupBy, uniq, isNumber, uniqBy } from 'lodash-es'
import { filter, pipe, sortBy, take } from 'lodash/fp'
import {
  AuditDetail,
  Direction,
  NegotiableFieldRequired,
  Negotiation,
  NegotiationDetail,
  NegotiationEvent,
  NegotiationStatus,
  TradeConditionFlag,
  UserPreference,
  NegotiationType,
  TradingType,
  SummaryRowNegotiation,
  OrderSummary,
  Order,
  OrderDetail,
  OrderDetailSummary,
  NegotiationHistory,
  Market,
  NegotiationOrAuditDetail,
  NegotiableFieldOptional,
  CollectionType,
  Collection,
  HaircutType,
  IsBestQuote,
  NegotiationDetailWithMeta,
  OrderAttributesCreateSource
} from 'api'
import {
  getBondPriceModificationType,
  getEndDateModificationType,
  getHaircutModificationType,
  getNotionalModificationType,
  getRateModificationType,
  getRFQTypeModificationType,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateModificationType,
  getTermModificationType,
  ModificationType
} from './deltaGetters'
import { getTimeLeft, MULTIPLE_VALUES } from 'utils'
import { getNotionalFormatOptions, getRateFormatOptions, negotiationSummaryRowDataGetter } from 'view/shared/datagrid'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { getClearingTicketInfo, getInitiatorTradingEntityTicketInfo, getUserAndOrganisationTicketInfo } from 'view/shared/ticket'
import { timeConstants } from 'view/components/timers'
import { BestPriceType, GlobalGridSettings } from 'store/grid/gridTypes'
import { CollectionTypeHelper } from 'model'
import { iconSet } from 'view/components/icons'
import { isNotNil } from 'utils/predicate'
import { getAllVisibleNodes, isAONSummary, TypedEditableCallbackParams, TypedRowNode } from 'view/components/datagrid'
import { GridApi, IRowNode } from 'ag-grid-community'

export const itemNegotiationTypes = [
  NegotiationType.INDIVIDUAL,
  NegotiationType.CONTINGENT_ITEM,
  NegotiationType.ONE_CANCELS_OTHERS_ITEM,
  NegotiationType.SMART_GROUPING_ITEM
]

export const summaryNegotiationTypes = [
  NegotiationType.CONTINGENT_SUMMARY,
  NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
  NegotiationType.SMART_GROUPING_SUMMARY,
  NegotiationType.AON_X_SUMMARY
]

export const isQuotable = (status: NegotiationStatus) => {
  const quotableStatuses = [
    NegotiationStatus.REQUESTED_WITHOUT_PRICE,
    NegotiationStatus.REQUESTED_WITH_PRICE,
    NegotiationStatus.QUOTED_SUBJECT,
    NegotiationStatus.WITHDRAWN
  ]
  return quotableStatuses.includes(status)
}

export const isCounterable = (status: NegotiationStatus) => {
  const counterableStatuses = [NegotiationStatus.QUOTED, NegotiationStatus.QUOTED_SUBJECT, NegotiationStatus.WITHDRAWN]
  return counterableStatuses.includes(status)
}

export const isImportantStatus = (status: NegotiationStatus) => {
  const importantStatuses = [
    NegotiationStatus.REQUESTED_WITH_PRICE,
    NegotiationStatus.REQUESTED_WITHOUT_PRICE,
    NegotiationStatus.QUOTED,
    NegotiationStatus.WITHDRAWN,
    NegotiationStatus.ACCEPTED,
    NegotiationStatus.REJECTED,
    NegotiationStatus.MISSED,
    NegotiationStatus.EXPIRED
  ]
  return importantStatuses.includes(status)
}

export const isWithdrawnOrSubject = (status: NegotiationStatus) => {
  const statuses = [NegotiationStatus.QUOTED_SUBJECT, NegotiationStatus.WITHDRAWN]
  return statuses.includes(status)
}

export const isTerminalStatus = (status: NegotiationStatus) => {
  const terminalStatuses = [
    NegotiationStatus.ACCEPTED,
    NegotiationStatus.REJECTED,
    NegotiationStatus.CANCELLED,
    NegotiationStatus.EXPIRED,
    NegotiationStatus.MISSED,
    NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_TERMINATED,
    NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED
  ]
  return terminalStatuses.includes(status)
}

export const isIncludedInBestPriceCalculationsStatus = (status: NegotiationStatus) => {
  const bestPriceStatuses = [NegotiationStatus.QUOTED, NegotiationStatus.QUOTED_SUBJECT]
  return bestPriceStatuses.includes(status)
}

export const isExcludedFromBestPriceCalculations = (negotiation: Negotiation) => {
  // exclude this negotiation from best price calculations if it has been changed
  // such that the trader now has to use their discretion in choosing best price
  return (
    !isSummaryNegotiationType(negotiation.negotiationType) &&
    (negotiation.startDate.negotiated !== negotiation.startDate.original ||
      negotiation.endDate.negotiated !== negotiation.endDate.original ||
      (negotiation.tradingType === TradingType.CASH
        ? negotiation.startConsideration.negotiated !== negotiation.startConsideration.original
        : negotiation.notional.negotiated !== negotiation.notional.original))
  )
}

export const isLastLook = (negotiation: Negotiation) => {
  return (
    (negotiation.status === NegotiationStatus.QUOTED_SUBJECT || negotiation.status === NegotiationStatus.QUOTED) &&
    !!negotiation.otwStartTime &&
    !!negotiation.otwEndTime &&
    moment(negotiation.otwStartTime).isSame(moment(negotiation.otwEndTime))
  )
}

interface ModifiedNegotiableFieldRequired<T> extends NegotiableFieldRequired<T> {
  modifiedValue: T
}

const isNegotiableFieldModified = <T>(field: NegotiableFieldRequired<T>): field is ModifiedNegotiableFieldRequired<T> => {
  return field.modified === true
}

export const getNegotiatedValue = <T>(field: NegotiableFieldRequired<T>) => {
  return isNegotiableFieldModified(field) ? field.modifiedValue : field.negotiated
}

export const getIsValidOTWDuration = (negotiationDetail: NegotiationDetail) => {
  return (
    !negotiationDetail.otwDuration ||
    moment.duration(negotiationDetail.otwDuration, 'minutes').asMilliseconds() < getTimeLeft(negotiationDetail.negotiation.rfqEndTime)
  )
}

export const canPerformEvent = (negotiationDetail: NegotiationDetail | AuditDetail, negotiationEvent: NegotiationEvent) => {
  return negotiationDetail.availableEvents.includes(negotiationEvent)
}

export const shouldStartEdit = (negotiationDetail: NegotiationDetail | AuditDetail | null | undefined) => {
  if (isNil(negotiationDetail) || isAuditDetail(negotiationDetail)) {
    return false
  }

  if (negotiationDetail.ownOrder) {
    return (
      (isCounterable(negotiationDetail.negotiation.status) && canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_ATTRIBUTES)) ||
      canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_REGULATORY_ATTRIBUTES)
    )
  } else {
    return (
      (isQuotable(negotiationDetail.negotiation.status) && canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_ATTRIBUTES)) ||
      canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_REGULATORY_ATTRIBUTES)
    )
  }
}

export const canUpdatePreAssignmentAttributes = (negotiationDetail: NegotiationDetail) => {
  return canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_PRE_ASSIGNMENT_ATTRIBUTES)
}

export const canUpdateAttributes = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return canPerformEvent(negotiationDetail, NegotiationEvent.UPDATE_ATTRIBUTES)
}

export const isNotHeaderRow = (params: TypedEditableCallbackParams<NegotiationDetail>) => {
  return !params.node.group
}

export const canRevert = (negotiationDetail: NegotiationDetail) => {
  if (negotiationDetail.ownOrder) {
    return canPerformEvent(negotiationDetail, NegotiationEvent.INITIATOR_REVERT_ATTRIBUTES)
  } else {
    return canPerformEvent(negotiationDetail, NegotiationEvent.RESPONDER_REVERT_ATTRIBUTES)
  }
}

const canSendSmartGroupLeg = (negotiationDetail: NegotiationDetail) => {
  return (
    negotiationDetail.negotiation.negotiationType === NegotiationType.SMART_GROUPING_ITEM &&
    isCounterable(negotiationDetail.negotiation.status) &&
    !!negotiationDetail.negotiation.modified
  )
}

export const canSend = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  if (isAuditDetail(negotiationDetail)) {
    return false
  } else if (negotiationDetail.ownOrder) {
    return (
      canPerformEvent(negotiationDetail, NegotiationEvent.COUNTER_OFFER) &&
      (!isWithdrawnOrSubject(negotiationDetail.negotiation.status) ||
        canSendSmartGroupLeg(negotiationDetail) ||
        canRevert(negotiationDetail) ||
        !!negotiationDetail.negotiation.summaryModified)
    )
  } else {
    return getIsValidOTWDuration(negotiationDetail) && canPerformEvent(negotiationDetail, NegotiationEvent.QUOTE)
  }
}

export const canRefresh = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  if (isAuditDetail(negotiationDetail)) {
    return false
  } else if (negotiationDetail.ownOrder) {
    return (
      canPerformEvent(negotiationDetail, NegotiationEvent.COUNTER_OFFER) &&
      isWithdrawnOrSubject(negotiationDetail.negotiation.status) &&
      !canRevert(negotiationDetail) &&
      !negotiationDetail.negotiation.summaryModified
    )
  } else {
    return false
  }
}

export const canAccept = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return !isAuditDetail(negotiationDetail) && canPerformEvent(negotiationDetail, NegotiationEvent.ACCEPT)
}

export const shouldHideAccept = (negotiationDetails: NegotiationDetail[]) => {
  const areThereContainerItems = negotiationDetails.filter(n => n.negotiation.containerCollectionId).length > 0

  if (!areThereContainerItems) return false

  const byContainer = groupBy(
    negotiationDetails.filter(n => n.negotiation.containerCollectionId),
    detail => detail.negotiation.containerCollectionId
  )

  const hasAnyContainerMoreThanOneCollectionSelected = Object.values(byContainer).some(
    details => uniqBy(details, d => d?.negotiation?.collectionId).length > 1
  )

  return hasAnyContainerMoreThanOneCollectionSelected
}

export const canReject = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return !isAuditDetail(negotiationDetail) && canPerformEvent(negotiationDetail, NegotiationEvent.REJECT)
}

export const canWithdraw = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return !isAuditDetail(negotiationDetail) && canPerformEvent(negotiationDetail, NegotiationEvent.WITHDRAW)
}

export const canCancel = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return !isAuditDetail(negotiationDetail) && canPerformEvent(negotiationDetail, NegotiationEvent.CANCEL)
}

export const canBackToStaging = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return (
    !isAuditDetail(negotiationDetail) &&
    !negotiationDetail.negotiation.sentBackToStaging &&
    canPerformEvent(negotiationDetail, NegotiationEvent.BACK_TO_STAGING)
  )
}

export const isClearedNegotiation = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  return isNotNil(negotiationDetail.negotiation.clearingHouseId)
}

export const isNonUSClearedNegotiation = (negotiationDetail: NegotiationDetail) => {
  return (
    negotiationDetail.usSponsoredRelationship === false &&
    negotiationDetail.negotiation.market !== Market.US &&
    isClearedNegotiation(negotiationDetail)
  )
}

export const getRateFromGlobalSettings = (negotiationDetail: NegotiationDetail, gridSettings: GlobalGridSettings) => {
  const isOwnOrder = negotiationDetail.ownOrder

  if (isOwnOrder) {
    return negotiationDetail.negotiation.direction === Direction.SELL ? gridSettings.offer : gridSettings.bid
  }

  return negotiationDetail.negotiation.direction === Direction.SELL ? gridSettings.bid : gridSettings.offer
}

export const getHaircutFromGlobalSettings = (negotiationDetail: NegotiationDetail, gridSettings: GlobalGridSettings) => {
  const isOwnOrder = negotiationDetail.ownOrder

  if (isOwnOrder) {
    return negotiationDetail.negotiation.direction === Direction.SELL ? gridSettings.offerHaircut : gridSettings.bidHaircut
  }

  return negotiationDetail.negotiation.direction === Direction.SELL ? gridSettings.bidHaircut : gridSettings.offerHaircut
}

export const getRFQTitle = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  const { ownOrder, orderFundFirm, counterpartyFirm, clearingHouse, sponsor, counterpartySponsor } = negotiationDetail
  const userAndOrganisationInfo = getUserAndOrganisationTicketInfo(
    ownOrder,
    orderFundFirm,
    counterpartyFirm,
    negotiationDetail.negotiation.lastInitiatorInitials,
    negotiationDetail.negotiation.lastResponderInitials
  )
  const clearingInfo = getClearingTicketInfo(clearingHouse, ownOrder, sponsor, counterpartySponsor)
  return `${userAndOrganisationInfo}${clearingInfo}`
}

export const getRFQKeyInfo = (negotiationDetail: NegotiationDetail | AuditDetail) => {
  const { ownOrder, orderFundFirm, orderFund, counterpartyFirm, clearingHouse, sponsor, counterpartySponsor } = negotiationDetail
  const userAndOrganisationInfo = getUserAndOrganisationTicketInfo(
    ownOrder,
    orderFundFirm,
    counterpartyFirm,
    negotiationDetail.negotiation.lastInitiatorInitials,
    negotiationDetail.negotiation.lastResponderInitials
  )
  const initiatorTradingEntity = getInitiatorTradingEntityTicketInfo(ownOrder, orderFund)
  const clearingInfo = getClearingTicketInfo(clearingHouse, ownOrder, sponsor, counterpartySponsor)

  return `${userAndOrganisationInfo}${initiatorTradingEntity}${clearingInfo}`
}

export const getRFQHeadline = (negotiationDetail: NegotiationDetail | AuditDetail, userPreference: UserPreference) => {
  const { negotiation, collateral, collateralBasket, collection, referenceRate } = negotiationDetail

  const instrumentName = isNil(collateral) ? (isNil(collateralBasket) ? '' : `${collateralBasket.name} `) : `${collateral.name} `
  const notionalString = `${Locale.formatNumber(
    isNegotiationHistory(negotiation) ? negotiation.notional : negotiation.notional.negotiated,
    getNotionalFormatOptions(userPreference.amountFormat, true)
  )} `
  const dateString = `${Locale.formatDate(
    isNegotiationHistory(negotiation) ? negotiation.startDate : negotiation.startDate.negotiated,
    userPreference.dateFormat
  )} - ${Locale.formatDate(
    isNegotiationHistory(negotiation) ? negotiation.endDate : negotiation.endDate.negotiated,
    userPreference.dateFormat
  )}`
  const collectionType = collection?.type ? `${CollectionTypeHelper.getName(collection.type)} ` : ''
  const weightedAverageRate = ` ${Locale.formatNumber(
    isNegotiationHistory(negotiation) ? negotiation.rate : negotiation.rate.negotiated,
    getRateFormatOptions(referenceRate)
  )} `

  return isSummaryNegotiationType(negotiation.negotiationType)
    ? `${collectionType}${notionalString}@${weightedAverageRate}${dateString}`
    : `${instrumentName}${notionalString}${dateString}`
}

export function getNegotiationTimeLeft(negotiation: Negotiation) {
  const rfqEndTime = negotiation.rfqEndTime
  const otwEndTime = negotiation.otwEndTime || rfqEndTime
  const endTime =
    negotiation.status === NegotiationStatus.QUOTED || negotiation.status === NegotiationStatus.QUOTED_SUBJECT
      ? otwEndTime
      : !isTerminalStatus(negotiation.status)
      ? rfqEndTime
      : null

  return endTime ? getTimeLeft(endTime) : null
}

export function getLiveStatusColor(negotiationAuditDetail: NegotiationDetail | AuditDetail, withExpiryWarning?: boolean) {
  if (negotiationAuditDetail.negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_SUMMARY) return ''
  if (canSend(negotiationAuditDetail)) {
    return constants.STATUS_COLOR_READY
  }

  if (negotiationAuditDetail.negotiation.negotiationType === NegotiationType.AON_X_SUMMARY) {
    return constants.TRANSPARENT
  }

  if (
    !isAuditDetail(negotiationAuditDetail) &&
    withExpiryWarning &&
    negotiationAuditDetail.negotiation.status === NegotiationStatus.QUOTED &&
    !isLastLook(negotiationAuditDetail.negotiation)
  ) {
    const otwEndTime = negotiationAuditDetail.negotiation.otwEndTime || negotiationAuditDetail.negotiation.rfqEndTime
    const timeLeft = getTimeLeft(otwEndTime)

    if (timeLeft < timeConstants.defaultUrgent) {
      return constants.STATUS_COLOR_URGENT
    } else if (timeLeft < timeConstants.defaultExpiring) {
      return constants.STATUS_COLOR_EXPIRING
    }
  }

  switch (negotiationAuditDetail.negotiation.status) {
    case NegotiationStatus.REQUESTED_WITHOUT_PRICE:
      return constants.STATUS_COLOR_AWAITING

    case NegotiationStatus.WITHDRAWN:
      return constants.STATUS_COLOR_INACTIVE

    case NegotiationStatus.REQUESTED_WITH_PRICE:
      if (!negotiationAuditDetail.negotiation.quotedAtLeastOnce) {
        return constants.STATUS_COLOR_AWAITING
      }
      return constants.STATUS_COLOR_ACTIVE

    case NegotiationStatus.QUOTED:
      return constants.STATUS_COLOR_RESPOND

    case NegotiationStatus.QUOTED_SUBJECT:
      return constants.STATUS_COLOR_ACTIVE

    case NegotiationStatus.CANCELLED:
    case NegotiationStatus.REJECTED:
    case NegotiationStatus.MISSED:
    case NegotiationStatus.EXPIRED:
      return constants.STATUS_COLOR_URGENT

    case NegotiationStatus.ACCEPTED:
      return constants.STATUS_COLOR_ACCEPTED

    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_TERMINATED:
      return constants.TRANSPARENT

    default:
      return constants.STATUS_COLOR_DEFAULT
  }
}

export const hasInitiatorRefreshedAnyNegotiableField = (negotiation: Negotiation) => {
  return (
    getNotionalModificationType(negotiation.notional) === ModificationType.INITIATOR ||
    getRateModificationType(negotiation.rate) === ModificationType.INITIATOR ||
    getHaircutModificationType(negotiation.haircut) === ModificationType.INITIATOR ||
    getStartConsiderationModificationType(negotiation.startConsideration) === ModificationType.INITIATOR ||
    getStartConsiderationOverrideModificationType(negotiation.startConsiderationOverride) === ModificationType.INITIATOR ||
    getBondPriceModificationType(negotiation.bondPrice) === ModificationType.INITIATOR ||
    getStartDateModificationType(negotiation.startDate) === ModificationType.INITIATOR ||
    getEndDateModificationType(negotiation.endDate) === ModificationType.INITIATOR ||
    getTermModificationType(negotiation.term) === ModificationType.INITIATOR ||
    getRFQTypeModificationType(negotiation.rfqType) === ModificationType.INITIATOR
  )
}

export const getLiveStatusText = (negotiationAuditDetail: NegotiationDetail | AuditDetail) => {
  const tradeConditionFlag = isAuditDetail(negotiationAuditDetail)
    ? TradeConditionFlag.NONE
    : negotiationAuditDetail.negotiation.tradeConditionFlag.negotiated || TradeConditionFlag.NONE

  switch (negotiationAuditDetail.negotiation.status) {
    case NegotiationStatus.REQUESTED_WITHOUT_PRICE:
      return negotiationAuditDetail.ownOrder
        ? Locale.translate(Translation.TC_AWAITING_RESPONSE)
        : Locale.translate(Translation.TC_NEW_REQUEST, { type: tradeConditionFlag })

    case NegotiationStatus.REQUESTED_WITH_PRICE:
      if (!negotiationAuditDetail.negotiation.quotedAtLeastOnce) {
        return !negotiationAuditDetail.ownOrder
          ? Locale.translate(Translation.TC_PRICED_NEW)
          : Locale.translate(Translation.TC_AWAITING_RESPONSE)
      }
      return negotiationAuditDetail.negotiation.refresh ||
        (!isAuditDetail(negotiationAuditDetail) && !hasInitiatorRefreshedAnyNegotiableField(negotiationAuditDetail.negotiation))
        ? Locale.translate(Translation.TC_REFRESHED)
        : negotiationAuditDetail.ownOrder
        ? Locale.translate(Translation.TC_COUNTERED)
        : Locale.translate(Translation.TC_COUNTER_IN)

    case NegotiationStatus.QUOTED:
      return negotiationAuditDetail.ownOrder ? Locale.translate(Translation.TC_PRICED) : Locale.translate(Translation.TC_QUOTED)

    case NegotiationStatus.QUOTED_SUBJECT:
      return Locale.translate(Translation.TC_QUOTE_SUBJECT)

    case NegotiationStatus.ACCEPTED:
      return Locale.translate(Translation.TC_ACCEPTED)

    case NegotiationStatus.REJECTED:
      return Locale.translate(Translation.TC_REJECTED)

    case NegotiationStatus.EXPIRED:
      return Locale.translate(Translation.TC_EXPIRED)

    case NegotiationStatus.MISSED:
      // FW BQ-1553 Fix to display correct status text based on lastResponder being present.
      // This assumes that lastResponder will not be present unless the Negotiation has been at status QUOTED
      // A proper fix to add a new status will be done on the server at some point in the future
      if (negotiationAuditDetail.negotiation.lastResponderId) {
        return negotiationAuditDetail.ownOrder ? Locale.translate(Translation.TC_REJECTED) : Locale.translate(Translation.TC_TRADED_AWAY)
      } else {
        return negotiationAuditDetail.ownOrder ? Locale.translate(Translation.TC_CANCELLED) : Locale.translate(Translation.TC_MISSED)
      }

    case NegotiationStatus.WITHDRAWN:
      return Locale.translate(Translation.TC_WITHDRAWN)

    case NegotiationStatus.CANCELLED:
      return Locale.translate(Translation.TC_CANCELLED)

    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_TERMINATED:
      return MULTIPLE_VALUES

    default:
      return '-- --'
  }
}

export function getLiveStatusTextPriced(negotiationDetail: NegotiationDetail | AuditDetail) {
  const { negotiation, ownOrder } = negotiationDetail
  switch (negotiation.status) {
    case NegotiationStatus.REQUESTED_WITH_PRICE:
    case NegotiationStatus.QUOTED_SUBJECT:
    case NegotiationStatus.WITHDRAWN:
      return ownOrder ? Locale.translate(Translation.TC_PRICED) : Locale.translate(Translation.TC_QUOTED)

    default:
      return getLiveStatusText(negotiationDetail)
  }
}

export const negotiationIsNewPriced = (negotiationDetail: NegotiationDetail) => {
  // We know an order is newPriced if REQUESTED_WITH_PRICE and !quotedAtLeastOnce
  const withPriced =
    negotiationDetail.negotiation.status === NegotiationStatus.REQUESTED_WITH_PRICE && !negotiationDetail.negotiation.quotedAtLeastOnce

  // Ideally we would just return that value, but they are some exceptions where we want to force False

  // For OCO summary we don't want to show the status
  if (negotiationDetail.negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_SUMMARY) return false

  // If it's your ownOrder, it doesn't apply either
  if (negotiationDetail.ownOrder) return false

  // If they modified the value, it's not priced (new) anymore
  if (negotiationDetail.negotiation.modified) return false

  return withPriced
}

export const getNegotiationStatusAsString = (negotiationDetail?: NegotiationDetail) => {
  if (
    !negotiationDetail ||
    negotiationDetail.negotiation.negotiationType === NegotiationType.AON_X_SUMMARY ||
    negotiationDetail.negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_SUMMARY
  )
    return null

  if (negotiationIsNewPriced(negotiationDetail)) {
    return Locale.translate(Translation.TC_PRICED_NEW)
  }
  if (canSend(negotiationDetail)) {
    return Locale.translate(Translation.TC_READY_TO_SEND)
  }

  return getLiveStatusText(negotiationDetail)
}

export function getLiveSubStatusText(negotiationDetail: NegotiationDetail | AuditDetail) {
  switch (negotiationDetail.negotiation.status) {
    case NegotiationStatus.QUOTED_SUBJECT:
      return Locale.translate(Translation.TC_SUBJECT)

    case NegotiationStatus.WITHDRAWN:
      return Locale.translate(Translation.TC_WITHDRAWN)

    case NegotiationStatus.REQUESTED_WITH_PRICE:
      if (!negotiationDetail.negotiation.quotedAtLeastOnce) {
        return !negotiationDetail.ownOrder
          ? Locale.translate(Translation.TC_PRICED_NEW)
          : Locale.translate(Translation.TC_AWAITING_RESPONSE)
      }
      return negotiationDetail.negotiation.refresh
        ? Locale.translate(Translation.TC_REFRESHED)
        : negotiationDetail.ownOrder
        ? Locale.translate(Translation.TC_COUNTERED)
        : Locale.translate(Translation.TC_COUNTER_IN)

    default:
      return ''
  }
}

export function getLiveIcon(status: NegotiationStatus) {
  switch (status) {
    case NegotiationStatus.QUOTED_SUBJECT:
      return iconSet.timerIcon

    case NegotiationStatus.CANCELLED:
    case NegotiationStatus.WITHDRAWN:
      return iconSet.noEntryIcon

    case NegotiationStatus.ACCEPTED:
      return iconSet.tickIcon

    case NegotiationStatus.EXPIRED:
    case NegotiationStatus.MISSED:
    case NegotiationStatus.REJECTED:
      return iconSet.thumbsDownIcon

    case NegotiationStatus.REQUESTED_WITH_PRICE:
    default:
      return null
  }
}

export const getNegotiationModificationColor = <T extends Pick<NegotiationDetail, 'negotiation' | 'ownOrder'>>(
  getModificationType: (negotiationDetail?: T) => ModificationType | null,
  negotiationDetail: T | null
) => {
  if (
    !negotiationDetail ||
    [NegotiationType.ONE_CANCELS_OTHERS_SUMMARY, NegotiationType.CONTINGENT_SUMMARY].includes(negotiationDetail.negotiation.negotiationType)
  ) {
    return null
  }

  const modificationType = getModificationType(negotiationDetail)

  if (
    (negotiationDetail.negotiation.status === NegotiationStatus.REQUESTED_WITHOUT_PRICE &&
      modificationType !== ModificationType.INTERNAL) ||
    isTerminalStatus(negotiationDetail.negotiation.status)
  ) {
    return null
  }

  switch (modificationType) {
    case ModificationType.INTERNAL:
      return constants.ACCENT_PRIMARY
    case ModificationType.INITIATOR:
      return negotiationDetail.ownOrder ? constants.ACCENT_PRIMARY_3 : constants.ACCENT_SECONDARY_2
    case ModificationType.RESPONDER:
      return negotiationDetail.ownOrder ? constants.ACCENT_SECONDARY_2 : constants.ACCENT_PRIMARY_3
    default:
      return null
  }
}

export function existsMultiEntityCollection(negotiationDetails: NegotiationDetail[]) {
  const negotiationDetailsByCollection = groupBy(
    negotiationDetails.filter(negotiationDetail => negotiationDetail.collection),
    negotiationDetail => negotiationDetail.collection?.id
  )
  return Object.values(negotiationDetailsByCollection).some(
    collectionNegotiationDetails =>
      uniq(
        collectionNegotiationDetails.map(negotiationDetail =>
          getNegotiatedValue(negotiationDetail.negotiation.counterpartyIdNegotiableField)
        )
      ).length > 1
  )
}

export const getNumberOfCounterparties = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(negotiations: T[]) => {
  return uniqBy(negotiations, n => n.counterparty?.id).length
}

export const getNumberOfQuotes = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(negotiations: T[]) => {
  return negotiations.flatMap(n => {
    const negotiatedValue = getNegotiatedValue(n.negotiation.rate)

    return !isNil(negotiatedValue) &&
      (n.negotiation.status === NegotiationStatus.QUOTED || n.negotiation.status === NegotiationStatus.QUOTED_SUBJECT)
      ? [negotiatedValue]
      : []
  }).length
}

/** for AON it returns the summary
 * for OCO it doesn't return best prices
 * for the rest of collections or individual orders returns the best priced leg/s
 **/
export const getBestPriceRowNodes = (gridApi: GridApi): IRowNode[] => {
  const nodes = getAllVisibleNodes(gridApi)

  const bestPriceNodes = nodes.filter(node => {
    if (node.group) {
      const summary = negotiationSummaryRowDataGetter(
        node as TypedRowNode<any>,
        gridApi?.getRowGroupColumns()?.map(rowGroupColumn => rowGroupColumn.getColId()) ?? [],
        gridApi?.getGridOption('context'),
        undefined
      )

      return summary && isAONSummary(summary) && summary?.meta?.isBestQuote === IsBestQuote.YES
    }

    if (
      node?.data?.collection?.type === CollectionType.ALL_OR_NOTHING ||
      node?.data?.collection?.type === CollectionType.ONE_CANCELS_OTHERS
    ) {
      return false
    }

    return node.data?.meta?.isBestQuote === IsBestQuote.YES
  })

  return bestPriceNodes
}

export const isBestPriceStatusSelected = (gridApi: GridApi): boolean => {
  const selectedNodes = gridApi.getSelectedNodes()

  return selectedNodes.every(node => {
    if (!node.group) {
      return node.data?.meta?.isBestQuote === IsBestQuote.YES
    }

    if (node.group) {
      const summary = negotiationSummaryRowDataGetter(
        node as TypedRowNode<any>,
        gridApi?.getRowGroupColumns()?.map(rowGroupColumn => rowGroupColumn.getColId()) ?? [],
        gridApi?.getGridOption('context'),
        undefined
      )

      return summary && isAONSummary(summary) && summary?.meta?.isBestQuote === IsBestQuote.YES
    }
  })
}

/** @deprecated
 * best price calculations are now done back end side, kept for reference
 */
export const getBestQuotesReceived = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  negotiations: T[],
  priceType = BestPriceType.RATE,
  count = 1
) => {
  if (negotiations.some(negotiation => negotiation.excludeFromBestPriceCalculations)) {
    return []
  }

  return pipe(
    filter<T>(({ negotiation: { rate, repoInterest } }) => isNumber(priceType === BestPriceType.RATE ? rate.negotiated : repoInterest)),
    filter(({ negotiation: { status } }) => isIncludedInBestPriceCalculationsStatus(status)),
    filter(({ negotiation: { modified } }) => !modified),
    sortBy([
      ({ negotiation: { rate, repoInterest, direction, negotiationType } }) => {
        const priceTypeFieldValue = priceType === BestPriceType.RATE ? rate.negotiated : repoInterest
        if (negotiationType === NegotiationType.CONTINGENT_SUMMARY) {
          return -1 * priceTypeFieldValue!
        }
        return direction === Direction.BUY ? -1 * priceTypeFieldValue! : priceTypeFieldValue
      },
      ({ negotiation: { lastQuotedAt } }) => (isNil(lastQuotedAt) ? '' : lastQuotedAt.valueOf())
    ]),
    take(count)
  )(negotiations)
}

export const getBestQuoteReceived = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(negotiations: T[]): T | undefined => {
  return negotiations.find((n: NegotiationDetailWithMeta) => n.meta?.isBestQuote === IsBestQuote.YES)
}

export const hasMatchingQuote = (negotiation: NegotiationDetail, negotiationsToCompare: NegotiationDetail[], priceType: BestPriceType) => {
  return negotiationsToCompare.some(
    candidate =>
      candidate.negotiation.negotiationId !== negotiation.negotiation.negotiationId &&
      !candidate.negotiation.modified &&
      isIncludedInBestPriceCalculationsStatus(candidate.negotiation.status) &&
      (priceType === BestPriceType.RATE
        ? candidate?.negotiation.rate.negotiated === negotiation?.negotiation.rate.negotiated
        : candidate?.negotiation?.repoInterest === negotiation?.negotiation?.repoInterest)
  )
}

const REQUESTED_STATUSES = [NegotiationStatus.REQUESTED_WITHOUT_PRICE, NegotiationStatus.REQUESTED_WITH_PRICE]

const subtractResponded = (count: number, negotiation: NegotiationDetail) =>
  REQUESTED_STATUSES.includes(negotiation.negotiation.status) ? count - 1 : count

export const getNegotiationPackageResponseCount = (negotiations: NegotiationDetail[]) =>
  negotiations.reduce(subtractResponded, negotiations.length)

export const isSummaryNegotiationType = (negotiationType: NegotiationType) => summaryNegotiationTypes.includes(negotiationType)

export const isItemNegotiationType = (negotiationType: NegotiationType) => itemNegotiationTypes.includes(negotiationType)

export const isEnhancedSummaryRowNegotiation = (negotiation: Negotiation): negotiation is SummaryRowNegotiation => {
  return negotiation.hasOwnProperty('summaryDirection')
}

export const isOrderSummary = (order: OrderSummary | Order): order is OrderSummary => {
  return order.hasOwnProperty('absoluteConsideration')
}

export const isOrderFix = (order: Order | OrderSummary | undefined): boolean => {
  return order !== undefined && !isOrderSummary(order) && isOrderFixAttribute(order)
}

const isOrderFixAttribute = (order: Order): boolean => order.source === OrderAttributesCreateSource.FIX

export const isOrderDetail = (orderDetail: OrderDetail | OrderDetailSummary): orderDetail is OrderDetail => {
  return orderDetail?.order?.hasOwnProperty('orderId')
}

export const isAuditDetail = (auditNegotiationDetail: NegotiationDetail | AuditDetail): auditNegotiationDetail is AuditDetail => {
  return auditNegotiationDetail.hasOwnProperty('auditId')
}

export const isNegotiationHistory = (
  negotiationOrHistory: Negotiation | NegotiationHistory
): negotiationOrHistory is NegotiationHistory => {
  return negotiationOrHistory.hasOwnProperty('latest')
}

export const isPricedByBothEntities = (field: NegotiableFieldOptional<number | HaircutType> | null | undefined): boolean => {
  const initiatorValue = isNotNil(field?.initiatorLastValue)
  const responderValue = isNotNil(field?.responderLastValue)
  return !!(initiatorValue && responderValue)
}

export const isSmartGroupCollection = <T extends { type?: CollectionType | null }>(collection: T | null | undefined): boolean => {
  return collection?.type === CollectionType.SMART_GROUPING
}

export const isSmartGroupOrderDetail = (orderDetail: OrderDetail | OrderDetailSummary | null): orderDetail is OrderDetail => {
  if (orderDetail) return isOrderDetail(orderDetail) && isSmartGroupCollection(orderDetail?.collection)
  return false
}

export const isSmartGroupOrderDetailSummary = (orderDetail: OrderDetail | OrderDetailSummary | null): orderDetail is OrderDetailSummary => {
  if (orderDetail) return !isOrderDetail(orderDetail) && isSmartGroupCollection(orderDetail?.collection)
  return false
}

export const isAONXCollection = <T extends { type?: CollectionType | null }>(collection: T | null | undefined): boolean => {
  return collection?.type === CollectionType.ALL_OR_NOTHING_X
}

export const isNestedCollection = <T extends { isNested: Collection['isNested'] }>(collection: T | null | undefined): boolean => {
  return !!collection?.isNested
}
